import React from 'react';
import {
    Platform,
    StyleProp,
    StyleSheet,
    Text,
    useWindowDimensions,
    View,
    ViewStyle,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { interRegular, interSemiBold } from '../../lib/fonts';
import PreviewResumeTrackingButton from './PreviewResumeTrackingButton';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface PreviewTrackingCardProps {
    projectName?: string | null;
    previewProjectId?: string | null;
    resultDigestId?: string | null;
    trialHref?: string | null;
    trackingState?: 'preparing' | 'paused' | 'pausing';
    style?: StyleProp<ViewStyle>;
    testID?: string;
}

export default function PreviewTrackingCard({
    projectName,
    previewProjectId,
    resultDigestId,
    trialHref,
    trackingState = 'paused',
    style,
    testID = 'preview-tracking-card',
}: PreviewTrackingCardProps) {
    const { i18n } = useLingui();
    const { width } = useWindowDimensions();
    const isNarrow = width < 600;
    const isPreparing = trackingState === 'preparing';
    const eyebrow = isPreparing
        ? i18n._(msg({ id: 'preview.tracking.preparingEyebrow', message: 'PREVIEW IS BEING PREPARED' }))
        : trackingState === 'paused'
            ? i18n._(msg({ id: 'preview.tracking.pausedEyebrow', message: 'TRACKING IS PAUSED' }))
            : i18n._(msg({ id: 'preview.tracking.pausingEyebrow', message: 'TRACKING IS PAUSING' }));
    const title = isPreparing
        ? i18n._(msg({ id: 'preview.tracking.preparingTitle', message: 'Your matches are almost ready' }))
        : i18n._(msg({ id: 'preview.tracking.resumeTitle', message: 'Keep tracking new papers' }));
    const body = isPreparing
        ? i18n._(msg({ id: 'preview.tracking.preparingBody', message: 'We found matching papers and are preparing your one-time preview. The feed stays limited while delivery finishes, then tracking will pause.' }))
        : trackingState === 'paused'
            ? projectName
                ? i18n._({ ...msg({ id: 'preview.tracking.pausedNamedBody', message: 'This was a one-time preview. Start your 14-day Starter trial to resume tracking for “{projectName}”, keep checking new publications, and receive new matches by digest email.' }), values: { projectName } })
                : i18n._(msg({ id: 'preview.tracking.pausedBody', message: 'This was a one-time preview. Start your 14-day Starter trial to resume tracking, keep checking new publications, and receive new matches by digest email.' }))
            : projectName
                ? i18n._({ ...msg({ id: 'preview.tracking.pausingNamedBody', message: 'This one-time preview is complete. Start your 14-day Starter trial now to keep tracking for “{projectName}” without interruption and receive new matches by digest email.' }), values: { projectName } })
                : i18n._(msg({ id: 'preview.tracking.pausingBody', message: 'This one-time preview is complete. Start your 14-day Starter trial now to keep tracking without interruption and receive new matches by digest email.' }));

    return (
        <View style={[styles.card, isNarrow && styles.cardNarrow, style]} testID={testID}>
            <View style={styles.pauseIcon}>
                <FontAwesome6 name="pause" size={24} color={colors.primaryGreenDark} />
            </View>

            <View style={styles.copy}>
                <Text style={[styles.eyebrow, interSemiBold]}>
                    {eyebrow}
                </Text>
                <Text
                    style={[styles.title, interSemiBold]}
                    accessibilityRole="header"
                    {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                >
                    {title}
                </Text>
                <Text style={[styles.body, interRegular]}>
                    {body}
                </Text>

                {!isPreparing ? (
                    <PreviewResumeTrackingButton
                        previewProjectId={previewProjectId}
                        resultDigestId={resultDigestId}
                        trialHref={trialHref}
                        testID="preview-resume-tracking-button-bottom"
                        label={trackingState === 'pausing'
                            ? msg({ id: 'preview.keepTrackingTrial', message: 'Keep tracking with a free trial' })
                            : undefined}
                    />
                ) : null}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 18,
        paddingHorizontal: 26,
        paddingVertical: 26,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: '#cfe2bf',
        backgroundColor: '#f3f8ee',
        ...(Platform.OS === 'web' && {
            boxShadow: '0 12px 32px rgba(44, 80, 50, 0.08)',
        }),
    },
    cardNarrow: {
        flexDirection: 'column',
        gap: 14,
        paddingHorizontal: 20,
        paddingVertical: 22,
    },
    pauseIcon: {
        width: 54,
        height: 54,
        borderRadius: 27,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#dcebcf',
        flexShrink: 0,
    },
    copy: {
        flex: 1,
        alignItems: 'flex-start',
        minWidth: 0,
    },
    eyebrow: {
        color: colors.primaryGreen,
        fontSize: 12,
        lineHeight: 17,
        letterSpacing: 0.8,
        marginBottom: 5,
    },
    title: {
        color: colors.primaryGreenDark,
        fontSize: 25,
        lineHeight: 32,
        marginBottom: 8,
    },
    body: {
        color: colors.text700,
        fontSize: 15,
        lineHeight: 23,
        maxWidth: 720,
    },
});
